import React, { useState, useEffect } from "react";
import {
  Button,
  Container,
  Form,
  ProgressBar,
  Row,
  Col,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import supabase from "../../supabase";
import { categories } from "../../constants/categories";

const WizardInscriptionArtisan = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDepartements, setSelectedDepartements] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [premiumOption, setPremiumOption] = useState("partenaire");
  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    const fetchDepartements = async () => {
    const { data, error } = await supabase
      .from("departements")
      .select("*")
      .order("numero");

    if (error) {
      console.error("Erreur chargement départements :", error.message);
    } else {
      
      setDepartements(data);
    }
  };

    fetchDepartements();
  }, []);

  const handleCategoryChange = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const handleDepartementChange = (departementId) => {
    setSelectedDepartements((prev) =>
      prev.includes(departementId)
        ? prev.filter((id) => id !== departementId)
        : [...prev, departementId]
    );
  };

  const selectAllCategories = () => setSelectedCategories([...categories]);
  const deselectAllCategories = () => setSelectedCategories([]);

  const selectAllDepartements = () =>
    setSelectedDepartements(departements.map((d) => d.id));
  const deselectAllDepartements = () => setSelectedDepartements([]);

  useEffect(() => {
    const numDepartements = selectedDepartements.length;
    const numCategories = selectedCategories.length;

    const calculatePrice = (departements, categories) => {
      if (departements === 0 || categories === 0) return 0;

      let baseUniquePrice = 9;
      if (premiumOption === "partenaire") {
        baseUniquePrice = 15;
      }

      return baseUniquePrice * departements * categories;
    };

    setTotalPrice(calculatePrice(numDepartements, numCategories));
  }, [selectedDepartements, selectedCategories, premiumOption]);

  const nextStep = () => setStep((prev) => Math.min(prev + 1, 3));
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const handleSubmit = () => {
    navigate("/formulaire-entreprise", {
      state: {
        selectedCategories,
        selectedDepartements,
        premiumOption,
        totalPrice,
      },
    });
  };

  return (
    <Container className="mt-5">
      <ProgressBar now={step * 33.33} className="mb-4" />

      {step === 1 && (
        <>
          <h2 className="text-center mb-3">
            Étape 1 : Sélectionnez vos métiers
          </h2>
          <div className="mb-3 d-flex gap-3 justify-content-center">
            <Button size="sm" variant="outline-secondary" onClick={selectAllCategories} style={{ borderColor: 'var(--primary-dark-text)', color: 'var(--primary-dark-text)' }}>
              Tout sélectionner
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={deselectAllCategories} style={{ borderColor: 'var(--primary-dark-text)', color: 'var(--primary-dark-text)' }}>
              Tout désélectionner
            </Button>
          </div>
          <Form>
            <Row>
              {categories
                .sort((a, b) => a.localeCompare(b))
                .map((cat, i) => (
                  <Col md={4} key={i}>
                    <Form.Check
                      type="checkbox"
                      label={cat}
                      checked={selectedCategories.includes(cat)}
                      onChange={() => handleCategoryChange(cat)}
                    />
                  </Col>
                ))}
            </Row>
          </Form>
          <div className="text-center mt-4">
            <Button
              onClick={nextStep}
              disabled={selectedCategories.length === 0}
              style={{ backgroundColor: 'var(--accent-orange)', borderColor: 'var(--accent-orange)' }}
            >
              Suivant
            </Button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <h2 className="text-center mb-3">
            Étape 2 : Choisissez vos départements
          </h2>
          <div className="mb-3 d-flex gap-3 justify-content-center">
            <Button size="sm" variant="outline-secondary" onClick={selectAllDepartements} style={{ borderColor: 'var(--primary-dark-text)', color: 'var(--primary-dark-text)' }}>
              Tout sélectionner
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={deselectAllDepartements} style={{ borderColor: 'var(--primary-dark-text)', color: 'var(--primary-dark-text)' }}>
              Tout désélectionner
            </Button>
          </div>
          <Form>
            <Row>
              {departements.map((dept) => (
                <Col md={4} key={dept.id}>
                  <Form.Check
                    type="checkbox"
                    label={`${dept.numero} - ${dept.nom}`}
                    checked={selectedDepartements.includes(dept.id)}
                    onChange={() => handleDepartementChange(dept.id)}
                  />
                </Col>
              ))}
            </Row>
          </Form>
          <div className="text-center mt-4">
            <Button onClick={prevStep} className="me-2" style={{ backgroundColor: 'var(--primary-dark-text)', borderColor: 'var(--primary-dark-text)' }}>
              Retour
            </Button>
            <Button
              onClick={nextStep}
              disabled={selectedDepartements.length === 0}
              style={{ backgroundColor: 'var(--accent-orange)', borderColor: 'var(--accent-orange)' }}
            >
              Suivant
            </Button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <h2 className="text-center mb-3">
            Étape 3 : Choisissez une option Boost
          </h2>
          <Form className="text-center">
            {/* PREMIUM */}
            <div
              className={`p-3 mb-3 border rounded ${
                premiumOption === "premium" ? "border-dark bg-light" : ""
              }`}
            >
              <Form.Check
                type="radio"
                id="premium"
                name="premium"
                checked={premiumOption === "premium"}
                onChange={() => setPremiumOption("premium")}
                label={
                  <div>
                    <span className="fw-bold">Devenir partenaire PREMIUM</span>
                    <div className="small mt-1 text-muted">
                      Les avantages partenaire et bien plus encore
                    </div>
                  </div>
                }
              />
            </div>

            {/* PARTENAIRE */}
            <div
              className={`p-3 mb-3 border rounded ${
                premiumOption === "partenaire" ? "border-warning bg-light" : ""
              }`}
            >
              <Form.Check
                type="radio"
                id="partenaire"
                name="premium"
                checked={premiumOption === "partenaire"}
                onChange={() => setPremiumOption("partenaire")}
                label={
                  <div>
                    <span className="fw-bold">Devenir partenaire</span>{" "}
                    <span className="badge bg-warning text-dark ms-2"
                    style={{ backgroundColor: 'var(--accent-orange)', color: 'white' }}
                    >
                      ✨ Recommandé
                    </span>
                    <div className="small mt-1 text-muted">
                      Apparaître jusqu’à 3× plus souvent dans les suggestions
                    </div>
                  </div>
                }
              />
            </div>

            {/* GRATUIT */}
            <div
              className={`p-3 mb-3 border rounded ${
                premiumOption === null ? "border-secondary bg-light" : ""
              }`}
            >
              <Form.Check
                type="radio"
                id="gratuit"
                name="premium"
                checked={premiumOption === null}
                onChange={() => setPremiumOption(null)}
                label={
                  <div>
                    <span className="fw-bold">Pas de boost</span>
                    <div className="small mt-1 text-muted">
                      Vous apparaîtrez selon vos critères de zone et métier
                      uniquement
                    </div>
                  </div>
                }
              />
            </div>
          </Form>

          <div className="text-center mt-4">
            <h4>
              Prix total :{" "}
              <strong style={{ color: "var(--accent-orange)" }}>
                {premiumOption === "premium"
                  ? "Sur devis"
                  : `${totalPrice}€ / an`}
              </strong>
            </h4>
            <Button onClick={prevStep} className="me-2" style={{ backgroundColor: 'var(--primary-dark-text)', borderColor: 'var(--primary-dark-text)' }}>
              Retour
            </Button>
            <Button variant="success" onClick={handleSubmit} style={{ backgroundColor: 'var(--accent-orange)', borderColor: 'var(--accent-orange)' }}>
              S'inscrire
            </Button>
          </div>
        </>
      )}

      {/* Avantages d'inscription */}
      <div
        className="mt-5 p-4 rounded"
        style={{ background: "linear-gradient(135deg, var(--secondary-light-bg), #fff)" }}
      >
        <h5
          className="text-center mb-4"
          style={{ color: "var(--accent-orange)", fontWeight: "bold" }}
        >
          🎯 Pourquoi rejoindre l'annuaire RenovEstim ?
        </h5>
        <Row className="text-center">
          <Col md={6} className="mb-3">
            <span style={{ fontSize: "1.5em" }}>📢</span>
            <p className="mt-2 mb-0">
              Soyez visible par les particuliers et professionnels
            </p>
          </Col>
          <Col md={6} className="mb-3">
            <span style={{ fontSize: "1.5em" }}>🔎</span>
            <p className="mt-2 mb-0">
              Apparaître dans les suggestions après chaque analyse de projet
            </p>
          </Col>
          <Col md={6} className="mb-3">
            <span style={{ fontSize: "1.5em" }}>🚀</span>
            <p className="mt-2 mb-0">
              Boostez votre notoriété locale et votre SEO
            </p>
          </Col>
          <Col md={6} className="mb-3">
            <span style={{ fontSize: "1.5em" }}>💼</span>
            <p className="mt-2 mb-0">
              Obtenez plus de chantiers qualifiés dans votre secteur
            </p>
          </Col>
          <Col md={6} className="mb-3">
            <span style={{ fontSize: "1.5em" }}>🏆</span>
            <p className="mt-2 mb-0">
              Valorisez votre image d’entreprise auprès de nouveaux clients
            </p>
          </Col>
          <Col md={6} className="mb-3">
            <span style={{ fontSize: "1.5em" }}>⚡</span>
            <p className="mt-2 mb-0">
              <strong style={{ color: "var(--accent-orange)" }}>
                Option Partenaire et Partenaire Premium
              </strong>{" "}
              : jusqu’à 5× plus de visibilité !
            </p>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default WizardInscriptionArtisan;
